<script setup lang="ts">
/**
 * One row inside a settings card: a title + description on the left and a
 * control on the right, separated from the previous row by a hairline.
 *
 * `stacked` drops the control below the text instead, for controls that need
 * the full card width (the AI prompt textarea).
 */
withDefaults(
	defineProps<{
		title: string;
		description?: string;
		required?: boolean;
		divided?: boolean;
		stacked?: boolean;
	}>(),
	{
		description: undefined,
		required: false,
		divided: true,
		stacked: false,
	},
);
</script>

<template>
	<div
		class="px-6 py-5"
		:class="{ 'border-t border-base-200': divided, 'space-y-4': stacked }"
	>
		<div
			class="flex gap-6"
			:class="stacked ? 'flex-col' : 'items-center justify-between'"
		>
			<div class="min-w-0">
				<p class="font-semibold text-base-content">
					{{ title }}
					<span v-if="required" class="text-error">*</span>
				</p>
				<p v-if="description" class="mt-1 text-sm text-sp-grey-300">
					{{ description }}
				</p>
			</div>

			<div v-if="!stacked" class="shrink-0">
				<slot name="control" />
			</div>
		</div>

		<slot v-if="stacked" name="control" />
	</div>
</template>
